/* eslint-disable complexity */
import { useCallback } from 'react';
import { PATHS } from '@ids/router/routes';
import useIDSPush from '@ids/hooks/useIDSPush';
import { useOverwhelmedOverlayDispatch } from '@ids/context/OverwhelmedOverlay';
import { useAppshellMeta, useFeatureToggle } from '@ids/appshell/hooks';
import { useCustomEvent, useSessionStorage } from '@ids/hooks';
import { useAppContext } from '@ids/context';
import { useAppshellRouter } from '@ids/appshell/RoutingHelper';
import { ensureEndingSlash, trimTrailingSlash } from '@ids/router/helpers';
import useOverstepPage, { START_OVER_KEY } from '@ids/pages/overstep/useOverstepPage';
import { SerializedSurveySparrowConfig } from '@ids/types/now-api';

export default function useQuestionnaireFlow() {
    const push = useIDSPush();
    const { isApp } = useAppshellMeta();
    const { host } = useAppshellRouter();
    const { location, idsPath } = useAppContext();
    const { p100IdsOverwhelmedOverlay, p100StudioPriceTiers } = useFeatureToggle();
    const { showOverwhelmedOverlay } = useOverwhelmedOverlayDispatch();
    const { openNativeOverlay } = useCustomEvent();
    const { getSessionStorageItem } = useSessionStorage();
    const { disableStartOver } = useOverstepPage();

    const startQuestionnaire = useCallback(
        async ({
            sku,
            surveySparrowConfig,
        }: { sku?: string; surveySparrowConfig?: SerializedSurveySparrowConfig } = {}) => {
            if (p100IdsOverwhelmedOverlay) {
                return showOverwhelmedOverlay();
            }

            const questionnaireURL = new URL(
                `${host}${trimTrailingSlash(idsPath)}${PATHS.QUESTIONNAIRE}/${location.search}`
            );

            if (
                p100StudioPriceTiers &&
                sku &&
                surveySparrowConfig?.formId &&
                surveySparrowConfig?.channelId &&
                surveySparrowConfig?.contactId &&
                surveySparrowConfig?.name
            ) {
                const queryParams = {
                    sku,
                    'form-id': surveySparrowConfig.formId,
                    'channel-id': surveySparrowConfig.channelId,
                    'contact-id': surveySparrowConfig.contactId,
                    name: surveySparrowConfig.name,
                };

                Object.entries(queryParams).forEach(([key, value]) => questionnaireURL.searchParams.append(key, value));

                const pushToQuestionnaire = () =>
                    push(`${PATHS.QUESTIONNAIRE}/${questionnaireURL.search}`, { withTrailingSlash: false });

                if (isApp) {
                    if (getSessionStorageItem(START_OVER_KEY)) {
                        disableStartOver();
                        return pushToQuestionnaire();
                    }
                    return openNativeOverlay(questionnaireURL.href);
                }
                return pushToQuestionnaire();
            }

            return isApp ? openNativeOverlay(questionnaireURL.href) : push(ensureEndingSlash(PATHS.QUESTIONNAIRE));
        },
        [
            p100IdsOverwhelmedOverlay,
            host,
            idsPath,
            location.search,
            p100StudioPriceTiers,
            isApp,
            openNativeOverlay,
            push,
            showOverwhelmedOverlay,
            getSessionStorageItem,
            disableStartOver,
        ]
    );

    return { startQuestionnaire };
}
